import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { FlagTriangleRight, ListChecks, Radio } from 'lucide-react';

import { FileDetails } from '../components/FileDetails';
import { HighRiskTransactions } from '../components/HighRiskTransactions';
import { KeyRiskSignals } from '../components/KeyRiskSignals';
import { RiskIndicators } from '../components/RiskIndicators';
import { RiskLevelCard } from '../components/RiskLevelCard';
import { StatWidgets } from '../components/StatWidgets';
import { TransactionPatterns } from '../components/TransactionPatterns';

type TransactionAnalysisSection = {
  id: string;
  title: string;
  Icon?: LucideIcon;
  Component: ReactNode;
  isHidden: boolean;
};

export type TransactionAnalysisData = {
  transactionAnalysisOutput: Record<string, unknown> | null | undefined;
  merchantName?: string | null;
  merchantId?: string | null;
  websitesCount?: number;
};

export const useTransactionAnalysisReportSections = (
  data: TransactionAnalysisData,
): { sections: TransactionAnalysisSection[] } => {
  const { transactionAnalysisOutput, merchantId, websitesCount } = data;

  const sections = useMemo(() => {
    const output = transactionAnalysisOutput;

    if (!output) {
      return [];
    }

    const executiveSummary = output.executiveSummary as
      | {
          overallRiskLevel: string;
          riskScore: number;
          keyFindings: string[];
          recommendedActions: string[];
        }
      | undefined;

    const riskIndicators = output.riskIndicators as Array<Record<string, unknown>> | undefined;
    const patterns = output.patterns as Array<Record<string, unknown>> | undefined;

    const riskLevel =
      (output.risk_level as string) ?? executiveSummary?.overallRiskLevel ?? 'unknown';

    const keyLaunderingSignals = output.key_laundering_signals as
      | Array<{ signal: string; summary: string; evidence: string[]; severity: string }>
      | undefined;

    const keyFindings =
      keyLaunderingSignals?.map(sig => sig.signal) ?? executiveSummary?.keyFindings;

    const overallAssessment = output.overall_assessment as
      | { detail_paragraph: string; summary_sentence: string }
      | undefined;

    const summaryMetrics = output.summary_metrics as
      | { critical_indicators: number; patterns_requiring_review: number }
      | undefined;

    const avgTransactionValue = output.avg_transaction_value as
      | { amount: number; currency: string }
      | undefined;

    const processingMccNumber = (output.processing_mcc_number as string) || 'unknown';
    const processingMccLabel = (output.processing_mcc_label as string) || 'Unknown';

    const geoDistribution = output.geo_distribution as
      | {
          primary_market_country: string;
          primary_market_percentage: number;
          full_breakdown: Array<{ country: string; percentage: number }>;
        }
      | undefined;

    const primaryMarkets: string[] = (() => {
      if (geoDistribution?.full_breakdown && geoDistribution.full_breakdown.length > 0) {
        return geoDistribution.full_breakdown.map(item => item.country);
      }

      if (
        geoDistribution?.primary_market_country &&
        geoDistribution.primary_market_country !== 'unknown'
      ) {
        return [geoDistribution.primary_market_country];
      }

      return [];
    })();

    const highRiskTransactions = output.high_risk_transactions as
      | Array<{ reference_numbers: string[]; label: string; reason: string; evidence: string[] }>
      | undefined;

    const dateRange = output.date_range as { start: string; end: string } | undefined;
    const totalTransactions = output.num_transactions as number | undefined;

    const allSections = [
      {
        id: 'risk-analysis',
        title: 'Risk Analysis',
        Icon: ListChecks,
        isHidden: false,
        Component: (
          <div className="space-y-8">
            <FileDetails
              dateRange={dateRange}
              websitesCount={websitesCount}
              totalTransactions={totalTransactions}
              merchantId={merchantId ?? undefined}
            />

            <RiskLevelCard level={riskLevel} keyFindings={keyFindings} />

            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-md">
              <div className="space-y-8">
                <h2 className="text-xl font-semibold" style={{ color: '#14203D' }}>
                  Risk Analysis
                </h2>

                <div>
                  <h3 className="font-semibold text-gray-900">Summary</h3>
                  {overallAssessment?.detail_paragraph && (
                    <p className="mt-1 text-sm leading-relaxed text-gray-700">
                      {overallAssessment.detail_paragraph}
                    </p>
                  )}
                </div>

                {summaryMetrics && (
                  <StatWidgets
                    metrics={summaryMetrics}
                    processingMccNumber={processingMccNumber}
                    processingMccLabel={processingMccLabel}
                    avgTransactionAmount={avgTransactionValue?.amount ?? 0}
                    avgTransactionCurrency={avgTransactionValue?.currency ?? 'USD'}
                    primaryMarkets={primaryMarkets}
                  />
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'key-risk-signals',
        title: 'Key Risk Signals',
        Icon: Radio,
        isHidden: false,
        Component: (
          <div className="space-y-8 pt-12">
            {keyLaunderingSignals && keyLaunderingSignals.length > 0 && (
              <KeyRiskSignals signals={keyLaunderingSignals} />
            )}

            <RiskIndicators indicators={riskIndicators} />
          </div>
        ),
      },
      {
        id: 'flagged-transactions',
        title: 'Flagged Transactions',
        Icon: FlagTriangleRight,
        isHidden: false,
        Component: (
          <div className="space-y-8 pb-32 pt-12">
            {highRiskTransactions && highRiskTransactions.length > 0 && (
              <HighRiskTransactions transactions={highRiskTransactions} />
            )}

            <TransactionPatterns patterns={patterns} />
          </div>
        ),
      },
    ] satisfies TransactionAnalysisSection[];

    return allSections.filter(section => !section.isHidden);
  }, [transactionAnalysisOutput, merchantId, websitesCount]);

  return { sections };
};
